import { Component, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RecentRecordsService } from '../../shared/recent-records/recent-records.service';
import { RiptSummaryService } from '../../features/ript/data/ript-summary.service';

/**
 * Salesforce-parity "RIPT Summaries" nav-tab wrapper. The label itself is a
 * plain routerLink (unchanged behaviour: navigates to the list). Only the
 * caret next to it opens a dropdown revealing the record-type chooser, the
 * last few visited summaries, and shortcuts to list views / new-tab.
 */
@Component({
  selector: 'app-ript-summaries-menu',
  standalone: true,
  imports: [RouterLink, MatIconModule, MatMenuModule],
  template: `
    <a routerLink="/ript" class="slds-context-bar__label-action" role="menuitem">
      RIPT Summaries
    </a>
    <button
      type="button"
      class="rsm-caret-btn"
      [matMenuTriggerFor]="menu"
      aria-haspopup="menu"
      aria-label="RIPT Summaries menu"
    >
      <mat-icon class="slds-context-bar__item-arrow">expand_more</mat-icon>
    </button>

    <mat-menu
      #menu="matMenu"
      class="rsm-menu"
      xPosition="after"
      yPosition="below"
      (menuOpened)="onMenuOpened()"
    >
      <button mat-menu-item type="button" (click)="onNew()">
        <mat-icon>add</mat-icon>
        <span>New RIPT Summary</span>
      </button>

      <div class="rsm-section-heading">Recent records</div>
      @if (records().length) {
        @for (rec of records(); track rec.id) {
          <button mat-menu-item type="button" (click)="onOpenRecord(rec.id)">
            <span class="rsm-record-title">{{ rec.title }}</span>
          </button>
        }
      } @else {
        <div class="rsm-empty">No recent records</div>
      }

      <div class="rsm-section-heading">Recent lists</div>
      <button mat-menu-item type="button" (click)="onOpenList('All')">
        <span>All</span>
      </button>

      <button mat-menu-item type="button" (click)="onOpenInNewTab()">
        <mat-icon>open_in_new</mat-icon>
        <span>Open "Recently Viewed | RIPT Summaries" in New Tab</span>
      </button>
    </mat-menu>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: stretch;
      }
      .rsm-caret-btn {
        background: transparent;
        border: 0;
        padding: 0 0.4rem;
        margin-left: -0.25rem;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        &:focus-visible {
          outline: 2px solid #fff;
          outline-offset: -2px;
        }
      }
      .rsm-caret-btn .slds-context-bar__item-arrow {
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
      }
    `,
  ],
})
export class RiptSummariesMenuComponent {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly recentRecords = inject(RecentRecordsService);
  private readonly summaryService = inject(RiptSummaryService);

  readonly records = this.recentRecords.records;

  /**
   * When the dropdown opens, verify each cached record still exists on the
   * server and drop any that have been deleted (a 404 prunes the stale entry).
   */
  onMenuOpened(): void {
    for (const rec of this.records()) {
      this.summaryService.getById(rec.id).subscribe({
        error: (err: unknown) => {
          if (this.isNotFound(err)) this.recentRecords.remove(rec.id);
        },
      });
    }
  }

  private isNotFound(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    return status === 404 || status === 410;
  }

  async onNew(): Promise<void> {
    const { RecordTypeChooserComponent } =
      await import('../../features/ript/create/record-type-chooser');
    const ref = this.dialog.open(RecordTypeChooserComponent, {
      width: '600px',
      autoFocus: true,
    });
    ref.afterClosed().subscribe((type: string | null | undefined) => {
      if (!type) return;
      void this.openCreateDialog(type);
    });
  }

  onOpenRecord(id: string): void {
    void this.router.navigate(['/ript', id]);
  }

  onOpenList(key: string): void {
    void this.router.navigate(['/ript'], { queryParams: { listView: key } });
  }

  onOpenInNewTab(): void {
    if (typeof window === 'undefined') return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/ript'], { queryParams: { listView: 'RecentlyViewed' } }),
    );
    window.open(url, '_blank', 'noopener');
  }

  private async openCreateDialog(recordType: string): Promise<void> {
    const isWorklist = recordType.toLowerCase() === 'worklist';
    const Component = isWorklist
      ? (await import('../../features/ript/form/worklist-form')).WorklistFormComponent
      : (await import('../../features/ript/form/standalone-form')).StandaloneFormComponent;

    const ref = this.dialog.open(Component as never, {
      width: '70vw',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: true,
      panelClass: 'ript-form-dialog',
    });
    ref
      .afterClosed()
      .subscribe((saved: { id?: string; name?: string; title?: string } | null | undefined) => {
        if (saved?.id) {
          this.recentRecords.push({ id: saved.id, title: saved.name || saved.title || saved.id });
          void this.router.navigate(['/ript', saved.id]);
        }
      });
  }
}
